/**
 * Cart Drawer — Custom element for Ajax cart drawer
 */
(function () {
  'use strict';

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('[data-cart-drawer-overlay]');
      this.body = this.querySelector('[data-cart-drawer-body]');
      this.sectionId = this.closest('[data-section-id]')?.dataset.sectionId || 'cart-drawer';
    }

    connectedCallback() {
      /* Close buttons (header ✕ + Continue Shopping) — delegated so newly-injected
         buttons after refresh() also fire close. */
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-drawer-close]')) this.close();
      });

      /* Overlay click */
      this.overlay?.addEventListener('click', () => this.close());

      /* Toggle event from header icon or product form */
      SixSeven.on('cart:toggle', () => {
        if (this.classList.contains('is-open')) {
          this.close();
        } else {
          this.open();
        }
      });

      /* After item added, refresh and open */
      SixSeven.on(SixSeven.Events.CART_ITEM_ADDED, () => {
        this.refresh().then(() => this.open());
      });

      /* Escape key */
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) {
          this.close();
        }
      });

      /* Quantity changes */
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-quantity-input]');
        if (!input) return;

        const item = input.closest('[data-cart-item]');
        if (!item) return;

        const line = parseInt(item.dataset.line);
        const quantity = parseInt(input.value);

        this.updateQuantity(line, quantity);
      });

      /* Remove item */
      this.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (!removeBtn) return;

        e.preventDefault();
        const line = parseInt(removeBtn.dataset.line);
        this.updateQuantity(line, 0);
      });

      /* Cart note — delegated input so it survives footer re-insertion after refresh() */
      let noteTimeout;
      this.addEventListener('input', (e) => {
        if (!e.target.matches('[data-cart-note]')) return;
        clearTimeout(noteTimeout);
        noteTimeout = setTimeout(() => {
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: e.target.value }),
          });
        }, 500);
      });
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    async updateQuantity(line, quantity) {
      this.classList.add('is-loading');

      try {
        const cart = await SixSeven.CartAPI.change(line, quantity);
        SixSeven.emit(SixSeven.Events.CART_UPDATED, cart);
        await this.refresh();
      } catch (err) {
        console.error('Cart update failed:', err);
      } finally {
        this.classList.remove('is-loading');
      }
    }

    async refresh() {
      try {
        const res = await fetch(
          `${window.location.pathname}?section_id=${this.sectionId}`
        );
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const newDrawer = parsed.querySelector('cart-drawer');
        if (!newDrawer) return;

        const container = this.querySelector('[data-cart-drawer-container]') || this;

        /* Update item count in header */
        const newCount = newDrawer.querySelector('[data-cart-drawer-count]');
        const currentCount = this.querySelector('[data-cart-drawer-count]');
        if (newCount && currentCount) currentCount.textContent = newCount.textContent;

        /* Sync a top-level block that may appear/disappear between renders.
           This is what fixes the empty→non-empty transition where the
           shipping-bar / upsell / footer were never inserted because the
           original markup didn't have them. */
        const syncBlock = (selector, insertBeforeSelector) => {
          const fresh = newDrawer.querySelector(selector);
          const current = this.querySelector(selector);
          if (fresh && current) {
            /* Both exist — update class (e.g. is-unlocked) + innerHTML */
            if (fresh.className !== current.className) current.className = fresh.className;
            current.innerHTML = fresh.innerHTML;
          } else if (fresh && !current) {
            /* Newly needed — insert at the correct position */
            const clone = fresh.cloneNode(true);
            const ref = insertBeforeSelector ? this.querySelector(insertBeforeSelector) : null;
            if (ref && container.contains(ref)) container.insertBefore(clone, ref);
            else container.appendChild(clone);
          } else if (!fresh && current) {
            /* No longer needed — remove */
            current.remove();
          }
        };

        /* Order in container: header, shipping-bar, body, upsell, footer */
        syncBlock('[data-shipping-bar]', '[data-cart-drawer-body]');

        /* Body — always present in the markup; just update its innerHTML */
        const newBody = newDrawer.querySelector('[data-cart-drawer-body]');
        const currentBody = this.querySelector('[data-cart-drawer-body]');
        if (newBody && currentBody) currentBody.innerHTML = newBody.innerHTML;
        this.body = currentBody;

        syncBlock('[data-cart-upsell]', '[data-cart-drawer-footer]');
        syncBlock('[data-cart-drawer-footer]', null);
      } catch (err) {
        console.error('Cart drawer refresh failed:', err);
      }
    }
  }

  customElements.define('cart-drawer', CartDrawer);

  /* Any cart trigger opens the drawer instead of navigating to /cart.
     Falls back to normal navigation when the drawer isn't on the page. */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-cart-toggle], [data-chm-cart]');
    if (!trigger) return;
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      e.preventDefault();
      drawer.open();
    }
  });
})();
